/**
 * useEngagementGraphGraphQL Hook
 * Feature: 080-critique-sharing
 *
 * Admin-only hooks for viewing engagement graph data.
 * Provides user relationship visualization and connection history.
 */

import { gql, useQuery, useLazyQuery } from '@apollo/client';

// ============================================================================
// GraphQL Queries
// ============================================================================

const GET_USER_ENGAGEMENT_GRAPH = gql`
  query GetUserEngagementGraph($userId: ID!) {
    userEngagementGraph(userId: $userId) {
      userId
      username
      displayName
      connectionCount
      registeredConnectionCount
      pendingConnectionCount
      connections {
        userId
        email
        username
        displayName
        avatarUrl
        isPending
        outboundCount
        inboundCount
        lastInteractionAt
      }
      stats {
        critiquesGiven
        critiquesReceived
        critiqueReplies
        sharesSent
        sharesReceived
        photosCurated
      }
    }
  }
`;

const GET_CONNECTION_HISTORY = gql`
  query GetConnectionHistory($userAId: ID!, $userBId: ID, $userBEmail: String) {
    connectionHistory(userAId: $userAId, userBId: $userBId, userBEmail: $userBEmail) {
      userA
      userB
      outboundEdges
      inboundEdges
      totalOutbound
      totalInbound
      firstInteractionAt
      lastInteractionAt
    }
  }
`;

const GET_USER_ENGAGEMENT_EDGES = gql`
  query GetUserEngagementEdges($userId: ID!, $limit: Int, $offset: Int) {
    userEngagementEdges(userId: $userId, limit: $limit, offset: $offset) {
      items
      totalCount
      hasMore
    }
  }
`;

const GET_ENGAGEMENT_GRAPH_STATS = gql`
  query GetEngagementGraphStats {
    engagementGraphStats {
      totalEdges
      totalConnections
      pendingEdges
      byInteractionType
      lastUpdated
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface EngagementStats {
  critiquesGiven: number;
  critiquesReceived: number;
  critiqueReplies: number;
  sharesSent: number;
  sharesReceived: number;
  photosCurated: number;
}

export interface ConnectionSummary {
  userId: string | null;
  email: string | null;
  username: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  isPending: boolean;
  outboundCount: number;
  inboundCount: number;
  lastInteractionAt: string;
}

export interface UserEngagementGraph {
  userId: string;
  username: string;
  displayName: string | null;
  connectionCount: number;
  registeredConnectionCount: number;
  pendingConnectionCount: number;
  connections: ConnectionSummary[];
  stats: EngagementStats;
}

export interface EdgeData {
  globalId: string;
  interactionType: string;
  interactionCount: number;
  firstInteractionAt: string;
  lastInteractionAt: string;
}

export interface UserInfo {
  userId: string | null;
  email?: string | null;
  username: string | null;
  displayName: string | null;
  isPending?: boolean;
}

export interface ConnectionDetail {
  userA: UserInfo;
  userB: UserInfo;
  outboundEdges: EdgeData[];
  inboundEdges: EdgeData[];
  totalOutbound: number;
  totalInbound: number;
  firstInteractionAt: string;
  lastInteractionAt: string;
}

export interface EngagementEdge {
  globalId: string;
  fromUserId: string;
  toUserId: string | null;
  toEmail: string | null;
  interactionType: string;
  interactionCount: number;
  firstInteractionAt: string;
  lastInteractionAt: string;
  isPending: boolean;
}

export interface EngagementEdgeConnection {
  items: EngagementEdge[];
  totalCount: number;
  hasMore: boolean;
}

export interface InteractionTypeCount {
  type: string;
  count: number;
}

export interface PlatformEngagementStats {
  totalEdges: number;
  totalConnections: number;
  pendingEdges: number;
  byInteractionType: InteractionTypeCount[];
  lastUpdated: string;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch engagement graph for a specific user
 * Admin only
 */
export function useUserEngagementGraph(userId: string | undefined) {
  const { data, loading, error, refetch } = useQuery<{ userEngagementGraph: UserEngagementGraph }>(
    GET_USER_ENGAGEMENT_GRAPH,
    {
      variables: { userId: userId || 'skip-placeholder' },
      skip: !userId,
      fetchPolicy: 'cache-and-network',
    }
  );

  return {
    data: data?.userEngagementGraph,
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
  };
}

/**
 * Lazy query for connection history between two users
 * Admin only
 */
export function useConnectionHistoryLazy() {
  const [getConnectionHistory, { data, loading, error }] = useLazyQuery<{
    connectionHistory: ConnectionDetail;
  }>(GET_CONNECTION_HISTORY, {
    fetchPolicy: 'network-only',
  });

  const fetchConnectionHistory = (userAId: string, userBId?: string, userBEmail?: string) => {
    return getConnectionHistory({
      variables: { userAId, userBId, userBEmail },
    });
  };

  return {
    fetchConnectionHistory,
    data: data?.connectionHistory,
    isLoading: loading,
    isError: !!error,
    error,
  };
}

/**
 * Fetch connection history between two users
 * Admin only
 */
export function useConnectionHistory(
  userAId: string | undefined,
  userBId?: string,
  userBEmail?: string
) {
  const { data, loading, error, refetch } = useQuery<{ connectionHistory: ConnectionDetail }>(
    GET_CONNECTION_HISTORY,
    {
      variables: {
        userAId: userAId || 'skip-placeholder',
        userBId,
        userBEmail,
      },
      skip: !userAId || (!userBId && !userBEmail),
      fetchPolicy: 'cache-and-network',
    }
  );

  return {
    data: data?.connectionHistory,
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
  };
}

/**
 * Fetch raw engagement edges for a user
 * Admin only
 */
export function useUserEngagementEdges(
  userId: string | undefined,
  limit: number = 50,
  offset: number = 0
) {
  const { data, loading, error, refetch, fetchMore } = useQuery<{
    userEngagementEdges: EngagementEdgeConnection;
  }>(GET_USER_ENGAGEMENT_EDGES, {
    variables: {
      userId: userId || 'skip-placeholder',
      limit,
      offset,
    },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  });

  const loadMore = () => {
    if (!data?.userEngagementEdges.hasMore) return;

    fetchMore({
      variables: {
        userId,
        limit,
        offset: (data?.userEngagementEdges.items.length || 0) + offset,
      },
    });
  };

  return {
    data: data?.userEngagementEdges,
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
    loadMore,
  };
}

/**
 * Fetch platform-wide engagement statistics
 * Admin only
 */
export function useEngagementGraphStats() {
  const { data, loading, error, refetch } = useQuery<{
    engagementGraphStats: PlatformEngagementStats;
  }>(GET_ENGAGEMENT_GRAPH_STATS, {
    fetchPolicy: 'cache-and-network',
  });

  return {
    data: data?.engagementGraphStats,
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format interaction type for display
 */
export function formatInteractionType(type: string): string {
  const typeMap: Record<string, string> = {
    critique_given: 'Comments Given',
    critique_received: 'Comments Received',
    critique_reply: 'Replies',
    share_sent: 'Shares Sent',
    share_received: 'Shares Received',
    collection_curated: 'Photos Curated',
    critique_session_viewed: 'Session Views',
    photo_viewed: 'Photo Views',
    CRITIQUE_GIVEN: 'Comments Given',
    CRITIQUE_RECEIVED: 'Comments Received',
    CRITIQUE_REPLY: 'Replies',
    SHARE_SENT: 'Shares Sent',
    SHARE_RECEIVED: 'Shares Received',
    COLLECTION_CURATED: 'Photos Curated',
    CRITIQUE_SESSION_VIEWED: 'Session Views',
    PHOTO_VIEWED: 'Photo Views',
  };

  return typeMap[type] || type;
}

/**
 * Get icon name for interaction type
 */
export function getInteractionTypeIcon(type: string): string {
  const iconMap: Record<string, string> = {
    critique_given: 'message-square',
    critique_received: 'message-square',
    critique_reply: 'reply',
    share_sent: 'share-2',
    share_received: 'share-2',
    collection_curated: 'folder-plus',
  };

  return iconMap[type.toLowerCase()] || 'activity';
}
